import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Edit, Globe, Server, Building, Package as PackageIcon } from 'lucide-react';
import { customersAPI, domainsAPI, companyPaymentsAPI, paymentsAPI } from '../lib/api';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [domains, setDomains] = useState([]);
  const [hostings, setHostings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    fetchCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      
      // Müşteri bilgilerini getir
      const customerData = await customersAPI.getById(id);
      const customer = customerData.data || customerData;
      setCustomer(customer);
      
      // Tüm domainleri getir
      const allDomains = await domainsAPI.getAll();
      
      // Tüm şirket ödemelerini getir
      const allPayments = await companyPaymentsAPI.getAll();
      
      // Ödemeleri getir (payments tablosu)
      let allPaymentsList = [];
      try {
        allPaymentsList = await paymentsAPI.getAll();
      } catch (paymentErr) {
        console.error('Ödemeler yüklenirken hata:', paymentErr);
      }
      
      // Müşterinin bağlı olduğu şirket ID'lerini topla
      const companyIds = customer.companies?.map(c => c.id) || [];
      
      // Müşteriye doğrudan bağlı domainler + müşterinin bağlı olduğu firmaların domainleri
      const customerDomains = allDomains.filter(d => {
        const directMatch = String(d.customer_id) === String(id);
        const companyMatch = companyIds.length > 0 && d.company_id && companyIds.includes(d.company_id);
        return directMatch || companyMatch;
      });
      setDomains(customerDomains);
      
      // Müşteriye doğrudan bağlı ödemeler + müşterinin bağlı olduğu firmaların ödemeleri
      const customerPayments = allPayments.filter(p => {
        const directMatch = String(p.customer_id) === String(id);
        const companyMatch = companyIds.length > 0 && p.company_id && companyIds.includes(p.company_id);
        return directMatch || companyMatch;
      });
      setHostings(customerPayments);
      
      // Müşteriye doğrudan bağlı payments tablosu ödemeleri
      const customerPaymentsList = allPaymentsList.filter(p => String(p.customer_id) === String(id));
      setPayments(customerPaymentsList);
      
    } catch (err) {
      console.error('Müşteri detayları yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  };

  // Hesaplama fonksiyonları
  const calculateStats = () => {
    const totalDomains = domains.length;
    const activeDomains = domains.filter(d => d.status === 'active').length;
    const expiredDomains = domains.filter(d => d.status === 'expired').length;
    
    const totalHostings = hostings.length;
    const activeHostings = hostings.filter(h => h.status === 'active').length;
    
    const totalPayments = payments.length;
    const paidPayments = payments.filter(p => p.status === 'completed').length;
    const pendingPayments = payments.filter(p => p.status === 'pending').length;
    
    const totalRevenue = payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    const pendingRevenue = payments
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    return {
      totalDomains, activeDomains, expiredDomains,
      totalHostings, activeHostings,
      totalPayments, paidPayments, pendingPayments,
      totalRevenue, pendingRevenue
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Müşteri bulunamadı
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-8 py-4">
          {/* Navigasyon */}
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
            <button onClick={() => navigate('/customers')} className="hover:text-blue-600">
              Müşteriler
            </button>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">{customer.name}</span>
          </div>

          {/* Başlık */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <User className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{customer.name}</h1>
                <p className="text-gray-600 dark:text-gray-400">{customer.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/customers', { state: { editCustomer: customer } })}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
              >
                <Edit size={18} />
                Düzenle
              </button>
              <button
                onClick={() => navigate('/customers')}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-2"
              >
                <ArrowLeft size={18} />
                Geri
              </button>
            </div>
          </div>

          {/* Sekmeler */}
          <div className="flex gap-1 mt-6 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('summary')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'summary'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Özet
            </button>
            <button
              onClick={() => setActiveTab('info')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'info'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Bilgiler
            </button>
            <button
              onClick={() => setActiveTab('domains')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'domains'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Domainler ({stats.totalDomains})
            </button>
            <button
              onClick={() => setActiveTab('hostings')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'hostings'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Hostingler ({stats.totalHostings})
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'payments'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Ödemeler ({stats.totalPayments})
            </button>
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="p-8">
        {activeTab === 'summary' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Müşteri Bilgileri */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Müşteri Bilgileri</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Ad Soyad</span>
                  <span className="font-medium text-gray-900 dark:text-white">{customer.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">E-Posta</span>
                  <span className="font-medium text-gray-900 dark:text-white">{customer.email}</span>
                </div>
                {customer.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">GSM</span>
                    <span className="font-medium text-gray-900 dark:text-white">{customer.phone}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Hesap Durumu</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    customer.is_active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {customer.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Kayıt Tarihi</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Date(customer.created_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              </div>
            </div>

            {/* Muhasebe */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Muhasebe</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Ödenmiş</span>
                  <span className="font-medium text-green-600">{stats.paidPayments} Ödeme</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Ödenmemiş</span>
                  <span className="font-medium text-red-600">{stats.pendingPayments} Ödeme</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Toplam Tahsilat</span>
                  <span className="font-bold text-gray-900 dark:text-white">
                    {stats.totalRevenue.toFixed(2)} ₺
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Beklenen Tahsilat</span>
                  <span className="font-bold text-orange-600">
                    {stats.pendingRevenue.toFixed(2)} ₺
                  </span>
                </div>
              </div>
            </div>

            {/* Ürün / Hizmetler */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ürün / Hizmetler</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-blue-600" />
                    <span className="text-gray-600 dark:text-gray-400">Domainler</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {stats.totalDomains} ({stats.activeDomains} aktif)
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Server size={16} className="text-purple-600" />
                    <span className="text-gray-600 dark:text-gray-400">Hostingler</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {stats.totalHostings} ({stats.activeHostings} aktif)
                  </span>
                </div>
                {customer.companies && customer.companies.length > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Building size={16} className="text-green-600" />
                      <span className="text-gray-600 dark:text-gray-400">Şirketler</span>
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {customer.companies.length}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Müşteri Hakkında Notlar */}
            {customer.notes && (
              <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Müşteri Hakkında Notlar</h3>
                <p className="text-gray-700 dark:text-gray-300">{customer.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'info' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Müşteri Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Ad Soyad</label>
                <p className="text-gray-900 dark:text-white">{customer.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">E-Posta</label>
                <p className="text-gray-900 dark:text-white">{customer.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Telefon</label>
                <p className="text-gray-900 dark:text-white">{customer.phone || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Adres</label>
                <p className="text-gray-900 dark:text-white">{customer.address || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Şehir</label>
                <p className="text-gray-900 dark:text-white">{customer.city || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Ülke</label>
                <p className="text-gray-900 dark:text-white">{customer.country || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Vergi No</label>
                <p className="text-gray-900 dark:text-white">{customer.tax_number || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Vergi Dairesi</label>
                <p className="text-gray-900 dark:text-white">{customer.tax_office || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Kayıt Tarihi</label>
                <p className="text-gray-900 dark:text-white">
                  {new Date(customer.created_at).toLocaleDateString('tr-TR')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Son Güncelleme</label>
                <p className="text-gray-900 dark:text-white">
                  {new Date(customer.updated_at).toLocaleDateString('tr-TR')}
                </p>
              </div>
            </div>

            {customer.notes && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Notlar</label>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded">
                  {customer.notes}
                </p>
              </div>
            )}

            {customer.companies && customer.companies.length > 0 && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">İlişkili Şirketler</label>
                <div className="space-y-2">
                  {customer.companies.map((company, idx) => (
                    <div key={company.id || idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <Building size={18} className="text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{company.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'domains' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Domainler ({stats.totalDomains})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Domain</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Durum</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Kayıt Tarihi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Bitiş Tarihi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Fiyat</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {domains.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        Kayıtlı domain yok
                      </td>
                    </tr>
                  ) : (
                    domains.map((domain, idx) => (
                      <tr key={domain.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Globe size={16} className="text-blue-600" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {domain.domain_name}.{domain.extension}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            domain.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : domain.status === 'expired'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {domain.status === 'active' ? 'Aktif' : domain.status === 'expired' ? 'Süresi Dolmuş' : domain.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {domain.registration_date ? new Date(domain.registration_date).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {domain.expiry_date ? new Date(domain.expiry_date).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                          {domain.purchase_price ? `${domain.purchase_price} ${domain.currency}` : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'hostings' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Hostingler / Tekrarlı Ödemeler ({stats.totalHostings})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Paket</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Alış</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Satış</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Kar</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {hostings.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-3 py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Kayıtlı hosting/ödeme yok
                      </td>
                    </tr>
                  ) : (
                    hostings.map((hosting, idx) => {
                      const purchasePrice = parseFloat(hosting.purchase_price || hosting.amount || 0);
                      const salePrice = parseFloat(hosting.sale_price || 0);
                      const profit = salePrice - purchasePrice;
                      return (
                        <tr key={hosting.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <PackageIcon size={12} className="text-purple-600 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {hosting.package_name || hosting.payment_type || 'Hosting'}
                                </div>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className={`inline-flex px-1 py-0.5 text-xs font-medium rounded ${
                                    hosting.package_type === 'domain'
                                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                      : hosting.package_type === 'hosting'
                                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                      : hosting.package_type === 'ssl'
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                      : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {hosting.package_type === 'domain' ? 'Domain'
                                      : hosting.package_type === 'hosting' ? 'Hosting'
                                      : hosting.package_type === 'ssl' ? 'SSL'
                                      : hosting.package_type || 'Diğer'}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    • {hosting.frequency === 'monthly' ? 'Aylık'
                                      : hosting.frequency === 'yearly' ? 'Yıllık'
                                      : hosting.frequency === 'quarterly' ? '3 Aylık'
                                      : hosting.frequency || '-'}
                                  </span>
                                </div>
                                {hosting.domain_name && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                                    <Globe size={10} />
                                    {hosting.domain_name}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {purchasePrice.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {hosting.purchase_currency || hosting.currency || 'TRY'}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="text-sm text-green-600 dark:text-green-400">
                              {salePrice > 0 ? salePrice.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {hosting.sale_currency || 'TRY'}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className={`text-sm font-semibold ${
                              profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              {profit !== 0 ? profit.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '-'}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                (hosting.is_active !== false && hosting.status === 'active')
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                  : hosting.status === 'paused'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              }`}>
                                {(hosting.is_active !== false && hosting.status === 'active') ? 'Aktif'
                                  : hosting.status === 'paused' ? 'Duraklatıldı'
                                  : hosting.status || 'Pasif'}
                              </span>
                              {hosting.next_payment_date && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(hosting.next_payment_date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ödemeler ({stats.totalPayments})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Tarih</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Tutar</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Ödeme Yöntemi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Durum</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Açıklama</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        Kayıtlı ödeme yok
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment, idx) => (
                      <tr key={payment.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {new Date(payment.payment_date).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                          {payment.amount} {payment.currency}
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {payment.payment_method || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            payment.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : payment.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {payment.status === 'completed' ? 'Tamamlandı' : payment.status === 'pending' ? 'Bekliyor' : payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {payment.description || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;
